/**
 * "Now uses account X (user Y), repaired by Z" (spec "Relink record"). X and Y come from the relink
 * snapshot, so the line stays true after that account is renamed or deleted. Dismiss is offered only
 * to the repairer or a Lookout admin, and never while unverified (the server decides `dismissible`).
 *
 * An unverified repair shows no separate notice: the unverified banner names the account instead.
 */
import type { RepairItemRef } from '@server/warehouse-repair';
import type { AccountRelinkNotice } from '@server/warehouse-repair-state';
import type { QueryKey } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { isOrpcNotFoundError, orpcErrorMessage } from '@/_shared/lib/orpc-errors';

import { relinkNoticeText } from './copy';
import { useDismissRelinkNotice } from './useAccountRepair';

export function RelinkNotice({
  notice,
  itemRef,
  invalidateKeys,
  repairEnabled,
  prefix,
}: {
  notice: AccountRelinkNotice | null;
  itemRef: RepairItemRef;
  invalidateKeys: readonly QueryKey[];
  repairEnabled: boolean;
  /** Names the workflow step the notice belongs to. */
  prefix?: string;
}): React.ReactElement | null {
  const { mutation: dismiss, invalidate } = useDismissRelinkNotice(invalidateKeys);
  if (!repairEnabled || !notice || notice.unverified) return null;
  const text = relinkNoticeText(notice);
  return (
    <Callout variant="info" className="items-center">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1">
          {prefix ? <span className="font-medium">{prefix}: </span> : null}
          {text}
        </p>
        {notice.dismissible && (
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            Icon={<X aria-hidden />}
            aria-label="Dismiss"
            loading={dismiss.isPending}
            onClick={() =>
              dismiss.mutate(
                { ref: itemRef },
                {
                  onError: (err) => {
                    // A repair landed in between (or the notice is already gone): reload what is there now.
                    if (isOrpcNotFoundError(err)) {
                      void invalidate();
                      toast.error('This notice changed. Showing the current account.');
                      return;
                    }
                    toast.error(orpcErrorMessage(err, 'Could not dismiss the notice'));
                  },
                },
              )
            }
          />
        )}
      </div>
    </Callout>
  );
}
